import type { HttpPort } from "~/lib/http";
import type { WeekInfo } from "~/features/teacher/WeekBanner";
import type { TreeData } from "~/features/today/today";

export interface StudentCheckin {
  morning_mood?: number | null;
  mood?: number | null;
  xp_earned?: number | null;
  daily_task_id?: string | null;
  daily_goal?: string | null;
  task_skipped?: boolean | null;
  daily_task_score?: number | null;
  class_goal_rating?: number | null;
  goal_1_rating?: number | null;
}

export interface StudentToday {
  has_checked_in: boolean;
  has_checked_out: boolean;
  checkin_data?: StudentCheckin | null;
  active_goals?: Array<{ rank?: number | null; title: string; description?: string | null }>;
  todays_tasks?: Array<{ id: string; task_text?: string | null; task_short?: string | null; goal_title?: string | null }>;
  mood_today?: Array<{ id: string; emoji: string; label: string }> | null;
  skip_used_this_week?: boolean;
}

export interface StudentMonster {
  name: string;
  level: number;
  experience_points: number;
}

export interface StudentProgress {
  checkins?: Array<{ checkout_time?: string | null }>;
  monster?: Pick<StudentMonster, "level" | "experience_points"> | null;
}

export interface StudentClassMood {
  total: number;
  checkedIn: number;
  checkedOut?: number;
  morningMoods: number[];
  eveningMoods: number[];
}

export interface StudentNotification {
  notification_type?: "voting_open" | "voting_open_voted" | "voting_closed" | "goals_set" | null;
  has_voted?: boolean;
  session?: { id?: string } | null;
}

export interface StudentHomeSnapshot {
  today: StudentToday;
  progress: StudentProgress;
  weekInfo: WeekInfo | null;
  monster: StudentMonster | null;
  xpForNextLevel: number | null;
  xpProgress: number;
  tree: TreeData | null;
  classMood: StudentClassMood | null;
  classGoal: { goal_text: string } | null;
  notification: StudentNotification | null;
  todayXp: number;
  dateLabel: string;
  greeting: string;
  timeLabel: string;
  weekendMessage: string | null;
}

const weekendMessages = [
  "Ausruhen, Erlebnisse sammeln und Energie für den nächsten Schultag tanken — dein Monster freut sich auf dich! 🌱",
  "Genieße dein Wochenende! Neue Eindrücke und Erholung machen dich morgen stärker.",
  "Kein Schultag heute — Zeit für dich, deine Familie und deine Hobbys. 🎮 Bis Montag!",
  "Pause ist genauso wichtig wie Lernen. Dein Monster hält die Stellung! 🛌"
];

export function createStudentHomeModule(http: HttpPort, token: string, clock: () => Date = () => new Date()) {
  const optional = async <T>(path: string): Promise<T | null> => {
    try { return await http.request<T>({ path, token }); }
    catch { return null; }
  };
  return {
    async load(classId: string): Promise<StudentHomeSnapshot> {
      const [today, progress, weekInfo, monsterPayload, tree, classMood, classGoalPayload, notification] = await Promise.all([
        http.request<StudentToday>({ path: `/checkins/${classId}/today`, token }),
        http.request<StudentProgress>({ path: `/students/${classId}/progress`, token }),
        optional<WeekInfo>(`/classes/${classId}/week-info`),
        optional<{ monster: StudentMonster; xpForNextLevel: number | null; xpProgress: number }>(`/students/${classId}/monster`),
        optional<TreeData>(`/classes/${classId}/tree`),
        optional<StudentClassMood>(`/checkins/${classId}/class-mood`),
        optional<{ goal: { goal_text: string } | null }>(`/class-goals/${classId}/today`),
        optional<StudentNotification>(`/voting/${classId}/notification-status`)
      ]);
      const now = clock();
      const hour = now.getHours();
      const weekend = (now.getDay() === 6 && !weekInfo?.weekend_config?.saturday) || (now.getDay() === 0 && !weekInfo?.weekend_config?.sunday);
      const checkin = today.checkin_data;
      return {
        today,
        progress,
        weekInfo,
        monster: monsterPayload?.monster ?? null,
        xpForNextLevel: monsterPayload?.xpForNextLevel ?? null,
        xpProgress: monsterPayload?.xpProgress ?? 0,
        tree,
        classMood,
        classGoal: classGoalPayload?.goal ?? null,
        notification,
        todayXp: (today.has_checked_in ? 5 + (checkin?.morning_mood != null ? 15 : 0) : 0) + (checkin?.xp_earned ?? 0),
        dateLabel: now.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" }),
        greeting: hour >= 5 && hour < 11 ? "Guten Morgen" : hour >= 11 && hour < 14 ? "Guten Mittag" : hour >= 14 && hour < 18 ? "Guten Nachmittag" : hour >= 18 && hour < 22 ? "Guten Abend" : "Gute Nacht",
        timeLabel: now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }),
        weekendMessage: weekend ? weekendMessages[now.getDate() % weekendMessages.length] : null
      };
    }
  };
}

export type StudentHomeModule = ReturnType<typeof createStudentHomeModule>;
